import { useEffect, useState } from "react";
import api, { brl } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [sos, setSos] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/financial/summary").then((r) => setSummary(r.data));
    api.get("/sales").then((r) => setSales(r.data));
    api.get("/service-orders").then((r) => setSos(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const topProducts = (() => {
    const map = {};
    sales.forEach((s) => (s.items || []).forEach((i) => {
      map[i.name] = (map[i.name] || 0) + i.quantity;
    }));
    return Object.entries(map).map(([name, qty]) => ({ name, qty })).sort((a, b) => b.qty - a.qty).slice(0, 10);
  })();

  const paymentMix = (() => {
    const map = {};
    sales.forEach((s) => { map[s.payment_method] = (map[s.payment_method] || 0) + s.total; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  })();

  const osByStatus = (() => {
    const map = {};
    sos.forEach((o) => { map[o.status] = (map[o.status] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  })();

  const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Análises</div>
        <h1 className="font-display text-4xl font-bold">Relatórios</h1>
      </header>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500">Vendas Total</div>
          <div className="font-display text-2xl font-bold">{sales.length}</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500">Receita Venda</div>
          <div className="font-display text-2xl font-bold text-emerald-600">{brl(sales.reduce((s, x) => s + x.total, 0))}</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500">OS Total</div>
          <div className="font-display text-2xl font-bold">{sos.length}</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500">Produtos</div>
          <div className="font-display text-2xl font-bold">{products.length}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg mb-4">Receita vs Despesa (6m)</h3>
          <div style={{ width: "100%", height: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="receita" fill="#10B981" />
                <Bar dataKey="despesa" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg mb-4">Mix de Pagamento</h3>
          <div style={{ width: "100%", height: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={paymentMix} dataKey="value" nameKey="name" outerRadius={80} label>
                  {paymentMix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg mb-4">Top Produtos</h3>
          <div style={{ width: "100%", height: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProducts} layout="vertical">
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="name" type="category" width={120} fontSize={11} />
                <Tooltip />
                <Bar dataKey="qty" fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <h3 className="font-display font-bold text-lg mb-4">OS por Status</h3>
          <div style={{ width: "100%", height: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={osByStatus} dataKey="value" nameKey="name" outerRadius={80} label>
                  {osByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
